<script>
import DashboardLayout from '@/components/layouts/dashboard/DashboardLayout';
import SidebarLink from '@/components/layouts/dashboard/SidebarLink';
import SidebarLinkGroup from '@/components/layouts/dashboard/SidebarLinkGroup';
import SiteForm from '@/components/sites/SiteForm';
import getErrorMessage from '@/modules/server-errors';

export default {
  name: 'NewSite',
  components: {
    DashboardLayout,
    SidebarLink,
    SidebarLinkGroup,
    SiteForm
  },
  metaInfo() {
    return { title: this.title };
  },
  data() {
    return {
      title: this.$t('New Site'),
      breadcrumbs: [{ text: this.$t('Sites'), to: { name: 'Sites' } }, { text: this.$t('New Site') }],
      invalid: [],
      isDisabled: false,
      isFavorite: false,
      isSaving: false,
      isTesting: false,
      site: {}
    };
  },
  mounted() {
    this.$refs.siteForm.$refs.name.focus();
  },
  beforeRouteLeave(to, from, next) {
    // Check for unsaved changes
    if (this.$refs.siteForm.isDirty()) {
      this.$confirm(this.$t("Your changes haven't been saved. Are you sure you want to continue?"), {
        title: this.$t('Unsaved Changes'),
        confirmButtonText: this.$t('Continue'),
        cancelButtonText: this.$t('Cancel')
      }).then(() => next());
    } else {
      next();
    }
  },
  methods: {
    // Cancels all changes and goes back to the sites page
    handleCancel() {
      this.$router.push({ name: 'Sites' });
    },

    // Saves the site
    handleSave() {
      const data = this.$refs.siteForm.getData();
      this.isSaving = true;
      this.$http
        .post('/sites', data)
        .then(res => {
          this.invalid = [];
          this.site = res.data.site;
        })
        .then(() => {
          // Add to favorites
          if (data.isFavorite) {
            return new Promise(resolve => {
              this.$http
                .post(`/users/${this.$store.state.user.id}/favorites/${this.site.id}`)
                .then(() => resolve())
                .catch(() => resolve());
            });
          }
        })
        .then(() => {
          // Enable notifications
          if (data.hasChangeNotification) {
            return new Promise(resolve => {
              this.$http
                .post(`/users/${this.$store.state.user.id}/change-notifications/${this.site.id}`)
                .then(() => resolve())
                .catch(() => resolve());
            });
          }
        })
        .then(() => {
          this.$refs.siteForm.resetCleanState();
          this.$router.push({ name: 'Site', params: { siteId: this.site.id } });
        })
        .catch(err => {
          const data = err.response.data;
          this.invalid = Array.isArray(data.invalid) ? data.invalid : [];
          this.$notify({
            type: 'error',
            title: this.$t('Error'),
            message: getErrorMessage(err.response.data.code)
          });
        })
        .finally(() => (this.isSaving = false));
    },

    // Tests the connection
    testConnection() {
      this.isTesting = true;
      this.$refs.siteForm.testConnection().then(() => (this.isTesting = false));
    }
  }
};
</script>

<template>
  <dashboard-layout size="small">
    <!-- Breadcrumbs -->
    <template slot="breadcrumbs">
      <el-breadcrumb separator-class="el-icon-arrow-right">
        <el-breadcrumb-item v-for="(item, index) in breadcrumbs" :key="index" :to="item.to">
          {{ item.text }}
        </el-breadcrumb-item>
      </el-breadcrumb>
    </template>

    <!-- Sidebar -->
    <template slot="sidebar">
      <!-- Actions -->
      <sidebar-link-group :title="$t('Actions')">
        <sidebar-link
          :text="$t('Test Connection')"
          :loading="isTesting"
          icon="el-icon-fa-plug"
          @click.native.prevent="testConnection"
        />
      </sidebar-link-group>
    </template>

    <!-- Site form -->
    <site-form
      ref="siteForm"
      :disabled="isDisabled"
      :invalid="invalid"
      :saving="isSaving"
      is-new
      @cancel="handleCancel"
      @submit="handleSave"
    />
  </dashboard-layout>
</template>

<style lang="scss" scoped>
@import '@/styles/variables.scss';
</style>
